import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { CatalogItem } from 'banesco-catalogues-api';
import { BnCataloguesService } from 'services';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'bn-contact-banesco-form',
  templateUrl: './contact-banesco-form.component.html',
})
export class BnContactBanescoFormComponent implements OnInit {
  @Input() hostRef: any;
  @Output() formChanged = new EventEmitter();
  @Output() dirtyStatusChange = new EventEmitter();
  @Output() currencyChanged = new EventEmitter();
  @Input() optionalFields = true;
  @Input() allowedProductTypes?: string[];
  @Input() selectedProductType?: string;
  @Input() customLabels?: { accountNumber: string };
  readonly creditCardId = '94';
  readonly infoTooltipText = $localize`:@@contact-manager.banesco-form.infoLabel:A short or friendly way to call your beneficiary`;
  readonly selectProductTypePlaceholder = $localize`:@@contact-manager.banesco-form.selectProductType:Select a product type`;
  readonly selectCurrencyPlaceholder = $localize`:@@contact-manager.other-form.selectCurrency:Select a currency`;
  destroy$ = new Subject<boolean>();
  productTypes: CatalogItem[] = [];
  currencies: CatalogItem[] = [];
  contactBanescoForm = new FormGroup({
    accountNumber: new FormControl('', [Validators.required, this.accountNumber()]),
    productType: new FormControl('', Validators.required),
    name: new FormControl(''),
    alias: new FormControl(''),
    email: new FormControl('', Validators.email),
    currency: new FormControl(),
  });
  loadingValidation = false;
  idError = '';

  constructor(private catalogueService: BnCataloguesService, private httpService: HttpClient) {}

  ngOnInit(): void {
    this.catalogueService
      .getProductTypes()
      .pipe(take(1))
      .subscribe((data) => {
        this.productTypes = this.allowedProductTypes
          ? data.filter((type) => !type.id || this.allowedProductTypes?.includes(type.id))
          : data;

        if (this.selectedProductType) {
          const selected = this.productTypes.find((type) => type.id === this.selectedProductType);
          this.contactBanescoForm.get('productType')?.setValue(selected);
          setTimeout(() => {
            this.enableAccountNumber();
          });
        }
      });
    this.contactBanescoForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((val) => {

      this.dirtyStatusChange.emit(this.contactBanescoForm.dirty);
      this.currencyChanged.emit(this.contactBanescoForm?.get('currency')?.value?.id);

      if (this.contactBanescoForm.valid) {
        this.formChanged.emit({
          contactsPostRequestBody: {
            accessContextScope: 'USER',
            name: val.name,
            category: 'banesco',
            alias: val.alias,
            emailId: val.email,
            additions: {
              idType: 'CIP',
              idNumber: '',
            },
            accounts: [
              {
                accountNumber: Number(val.accountNumber.replace(/\s/g, '')),
                bankName: 'Banesco',
                additions: {
                  productType: val.productType.id,
                  currencyCode: val?.currency?.id,
                },
              },
            ],
          },
        });
      } else {
        this.formChanged.emit(undefined);
      }
    });

    this.contactBanescoForm
      .get('productType')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        this.contactBanescoForm.get('accountNumber')?.patchValue('');
        this.contactBanescoForm.get('accountNumber')?.updateValueAndValidity();
      });

    this.getCurrencies()
      .pipe(take(1))
      .subscribe((data) => {
        this.currencies = data.map(({ id, desc }) => ({ id, desc: id ? `${id}$ - ${desc}` : '' }));
      });

    this.getAccountNumberObservable();
  }

  getCurrencies(): Observable<CatalogItem[]> {
    return this.catalogueService.getCurrencies();
  }

  enableAccountNumber(): void {
    this.contactBanescoForm?.get('accountNumber')?.enable();
  }

  accountNumber(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent!) {
        return null;
      }
      const productType = control.parent!.get('productType')?.value;
      if (control.value && productType) {
        const value = control.value.replace(/\s/g, '');
        const isnum = /^\d+$/.test(value);

        if (!isnum) {
          return { accountNumberFormat: true };
        }

        let length = 12;
        if (productType.id === this.creditCardId) {
          length = 16;
        }

        if (value.length > length) {
          if (productType.id === this.creditCardId) {
            return { credit: true };
          } else {
            return { account: true };
          }
        }
        return null;
      }

      return null;
    };
  }

  getAccountNumberObservable() {
    const accountNumber$ = this.contactBanescoForm.get('accountNumber')?.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      filter((value) => this.isFullLength(value) || this.isCcFullLength(value))
    );

    const validAccountNumber$ = accountNumber$?.pipe(
      tap((_) => {
        this.contactBanescoForm.get('name')?.patchValue('');
        this.loadingValidation = true;
      }),
      switchMap((value) => {
        return this.httpService.post(
          `/api/contact-aux-service/v1/getBeneficiaryName`,
          {
            productType: this.contactBanescoForm.get('productType')?.value.id,
            productNumber: value
          }
          ).pipe(
            catchError((err) => {
              console.error(err);
              this.loadingValidation = false;
              if (err.error?.message) {
                this.idError = err.error.message;
              } else {
                this.idError = $localize`:@@contact-manager.banesco-form.idError:There was an error with the validation service`;
              }
              this.contactBanescoForm.get('accountNumber')?.setErrors({ invalidNumber: true });
              return of(null);
            })
          )
      }),
      tap((res: any) => {
        this.loadingValidation = false;
        this.contactBanescoForm.get('name')?.patchValue(res?.name);
      })
    );
    validAccountNumber$?.subscribe();
  }

  get accountNumberMask() {
    if (this.contactBanescoForm.get('productType')?.value.id === '94') {
      return 'XXXX XXXX XXXX XXXX';
    }
  }

  isCcFullLength(value: string) {
    return this.contactBanescoForm.get('productType')?.value?.id === '94' && value.length === 16;
  }

  isFullLength(value: string) {
    return ['01', '04', '03', '10'].includes(this.contactBanescoForm.get('productType')?.value?.id) && 10 <= value.length && value.length <= 12;
  }
}
